import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('dotcall_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('dotcall_token');
      localStorage.removeItem('dotcall_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleAuth: (data) => api.post('/auth/google', data),
  googleClientId: () => api.get('/auth/google-client-id'),
  completeGoogleRegistration: (data) => api.post('/auth/google/complete', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

export const agentsAPI = {
  list: () => api.get('/agents'),
  get: (id) => api.get(`/agents/${id}`),
  create: (data) => api.post('/agents', data),
  update: (id, data) => api.put(`/agents/${id}`, data),
  delete: (id) => api.delete(`/agents/${id}`),
  regenerateGreeting: (id) => api.post(`/agents/${id}/regenerate-greeting`),
  aiFill: (data) => api.post('/agents/ai-fill', data),
  voicePreview: (voice, language) => api.get('/agents/voice-preview', {
    params: { voice, language },
    responseType: 'blob'
  }),
};

export const campaignsAPI = {
  list: () => api.get('/campaigns'),
  get: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  start: (id) => api.put(`/campaigns/${id}/start`),
  pause: (id) => api.put(`/campaigns/${id}/pause`),
  rerun: (id) => api.put(`/campaigns/${id}/rerun`),
  delete: (id) => api.delete(`/campaigns/${id}`),
  details: (id) => api.get(`/campaigns/${id}/details`),
  uploadContacts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/campaigns/upload-contacts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const callsAPI = {
  list: () => api.get('/calls'),
  get: (id) => api.get(`/calls/${id}`),
};

export const phoneNumbersAPI = {
  search: (params) => api.get('/phone-numbers/search', { params }),
  purchase: (data) => api.post('/phone-numbers/purchase', data),
  list: () => api.get('/phone-numbers'),
  setDefault: (id) => api.put(`/phone-numbers/${id}/set-default`),
  release: (id) => api.delete(`/phone-numbers/${id}`),
  updateInbound: (id, data) => api.put(`/phone-numbers/${id}/inbound-settings`, data),
};

export const portingAPI = {
  create: (data) => api.post('/porting-requests', data),
  list: () => api.get('/porting-requests'),
  cancel: (id) => api.delete(`/porting-requests/${id}`),
};

export const ecommerceAPI = {
  getConfig: () => api.get('/ecommerce/config'),
  updateConfig: (data) => api.put('/ecommerce/config', data),
  getLogs: () => api.get('/ecommerce/logs'),
  downloadPlugin: () => api.get('/ecommerce/woocommerce-plugin', { responseType: 'blob' }),
};

export const integrationsAPI = {
  list: () => api.get('/integrations'),
  save: (data) => api.put('/integrations', data),
  test: (name) => api.post(`/integrations/${name}/test`),
};

export const oncallLeadsAPI = {
  list: (params = {}) => api.get('/oncall-leads', { params }),
  get: (id) => api.get(`/oncall-leads/${id}`),
  update: (id, data) => api.put(`/oncall-leads/${id}`, data),
  delete: (id) => api.delete(`/oncall-leads/${id}`),
  sendWhatsappDemo: (id) => api.post(`/oncall-leads/${id}/send-whatsapp-demo`),
};

export const knowledgeBaseAPI = {
  list: () => api.get('/knowledge-base'),
  create: (data) => api.post('/knowledge-base', data),
  delete: (id) => api.delete(`/knowledge-base/${id}`),
  search: (query) => api.post('/knowledge-base/search', { query }),
};

export const analyticsAPI = {
  overview: () => api.get('/analytics/overview'),
  charts: () => api.get('/analytics/charts'),
};

export const chatAPI = {
  send: (data) => api.post('/chat', data),
  history: (agentId, sessionId) => api.get(`/chat/history/${agentId}${sessionId ? `?session_id=${sessionId}` : ''}`),
};

export const billingAPI = {
  plans: () => api.get('/billing/plans'),
  createOrder: (planId) => api.post(`/billing/create-order?plan_id=${planId}`),
  verifyPayment: (data) => api.post('/billing/verify-payment', data),
  subscribe: (planId) => api.post(`/billing/subscribe?plan_id=${planId}`),
  usage: () => api.get('/billing/usage'),
  razorpayKey: () => api.get('/billing/razorpay-key'),
  createRegistrationOrder: (data) => api.post('/billing/create-registration-order', data),
  verifyRegistrationPayment: (data) => api.post('/billing/verify-registration-payment', data),
  planChangePreview: (targetPlanId) => api.get(`/billing/plan-change-preview?target_plan_id=${targetPlanId}`),
  createUpgradeOrder: (targetPlanId) => api.post(`/billing/create-upgrade-order?target_plan_id=${targetPlanId}`),
  changePlan: (data) => api.post('/billing/change-plan', data),
};

export const walletAPI = {
  balance: () => api.get('/wallet/balance'),
  transactions: () => api.get('/wallet/transactions'),
  addFunds: (amount) => api.post(`/wallet/add-funds?amount=${amount}`),
  verifyPayment: (data) => api.post('/wallet/verify-payment', data),
};

export const showcasesAPI = {
  list: () => api.get('/demo-showcases'),
};


export const seedDemo = () => api.post('/seed-demo');

export default api;
